import React, { useState, useEffect } from 'react';
import { getInvites, createInvite, deleteInvite } from '../services/invites';
import { InviteEntry } from '../generated-api';
import { TrashIcon, ClipboardDocumentIcon, ShareIcon, PlusIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';

const EXPIRY_OPTIONS = [
  { label: '24 hours', value: 24 },
  { label: '48 hours', value: 48 },
  { label: '72 hours', value: 72 },
  { label: '1 week', value: 168 },
  { label: '30 days', value: 720 },
];

const InvitesPage: React.FC = () => {
  const [invites, setInvites] = useState<InviteEntry[]>([]);
  const [label, setLabel] = useState('');
  const [expiresInHours, setExpiresInHours] = useState(168);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  useEffect(() => {
    fetchInvites();
  }, []);

  const fetchInvites = async () => {
    setLoading(true);
    try {
      const data = await getInvites();
      setInvites(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch invites');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createInvite(label || undefined, expiresInHours);
      setLabel('');
      setError(null);
      fetchInvites();
    } catch (err) {
      setError('Failed to create invite');
    }
  };

  const handleDelete = async (token: string) => {
    try {
      await deleteInvite(token);
      setError(null);
      fetchInvites();
    } catch (err) {
      setError('Failed to delete invite');
    }
  };

  const handleCopy = async (token: string) => {
    const url = window.location.origin;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      setError('Failed to copy link');
    }
  };

  const handleShare = async () => {
    const url = window.location.origin;
    try {
      await navigator.share({
        title: 'Board Game Stats',
        text: 'Join our board game group!',
        url,
      });
    } catch {
      // User cancelled or share failed
    }
  };

  const activeInvites = invites.filter((i) => i.active);
  const expiredInvites = invites.filter((i) => !i.active);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader className="mb-8">
        <h1 className="text-fluid-h1 font-bold text-heading">Invite Management</h1>
        <p className="mt-2 text-body">
          Create and manage invite links. An active invite allows new users to sign up.
        </p>
      </PageHeader>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="bg-surface overflow-hidden shadow rounded-lg mb-8">
        <div className="p-5">
          <h3 className="text-lg font-medium text-heading mb-4">Create Invite</h3>
          <form onSubmit={handleCreate} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-grow">
              <label htmlFor="invite-label" className="sr-only">Label</label>
              <input
                id="invite-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Optional label (e.g. 'For Alice')"
                className="w-full px-4 py-2 border border-edge-input rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label htmlFor="invite-expiry" className="sr-only">Expiry</label>
              <select
                id="invite-expiry"
                value={expiresInHours}
                onChange={(e) => setExpiresInHours(Number(e.target.value))}
                className="w-full px-4 py-2 border border-edge-input rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              >
                {EXPIRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Invite
            </button>
          </form>
        </div>
      </div>

      <div className="bg-surface shadow rounded-lg">
        <div className="px-6 py-4 border-b border-edge">
          <h3 className="text-lg font-medium text-heading">Active Invites</h3>
        </div>
        <ul className="divide-y divide-edge">
          {activeInvites.length > 0 ? (
            activeInvites.map((invite) => (
              <li key={invite.token} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-heading">
                    {invite.label || 'No label'}
                  </p>
                  <p className="text-sm text-muted">
                    Created by {invite.createdBy} &middot; Expires {new Date(invite.expiresAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    data-track="invite_copy"
                    onClick={() => handleCopy(invite.token)}
                    className="inline-flex items-center px-3 py-1.5 border border-edge-input text-xs font-medium rounded-md text-body bg-surface hover:bg-surface-hover"
                    title="Copy link"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4 mr-1.5" />
                    {copiedToken === invite.token ? 'Copied!' : 'Copy Link'}
                  </button>
                  {typeof navigator.share === 'function' && (
                    <button
                      data-track="invite_share"
                      onClick={handleShare}
                      className="inline-flex items-center px-3 py-1.5 border border-edge-input text-xs font-medium rounded-md text-body bg-surface hover:bg-surface-hover"
                      title="Share"
                    >
                      <ShareIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    data-track="invite_delete"
                    onClick={() => handleDelete(invite.token)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                  >
                    <TrashIcon className="h-4 w-4 mr-1.5" />
                    Delete
                  </button>
                </div>
              </li>
            ))
          ) : (
            <li className="px-6 py-4 text-center text-muted">No active invites.</li>
          )}
        </ul>
      </div>

      {expiredInvites.length > 0 && (
        <div className="bg-surface shadow rounded-lg mt-8">
          <div className="px-6 py-4 border-b border-edge">
            <h3 className="text-lg font-medium text-heading">Expired Invites</h3>
          </div>
          <ul className="divide-y divide-edge">
            {expiredInvites.map((invite) => (
              <li key={invite.token} className="px-6 py-4 flex items-center justify-between opacity-60">
                <div>
                  <p className="text-sm font-medium text-heading">
                    {invite.label || 'No label'}
                  </p>
                  <p className="text-sm text-muted">
                    Created by {invite.createdBy} &middot; Expired {new Date(invite.expiresAt).toLocaleString()}
                  </p>
                </div>
                <button
                  data-track="invite_delete"
                  onClick={() => handleDelete(invite.token)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                >
                  <TrashIcon className="h-4 w-4 mr-1.5" />
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InvitesPage;
